import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { useSidebar } from "@src/lib/ui/Sidebar";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import AdminPrimaryNavRail from "./AdminPrimaryNavRail";
import AdminSecondaryNavMenu from "./AdminSecondaryNavMenu";
import {
  AdminNavSectionId,
  adminAccountPlanNavItem,
  adminSettingsNavItem,
  findActivePrimaryItem,
  getSettingsLandingHref,
  getVisiblePrimaryItems,
  getVisibleSecondaryItems,
} from "./adminNavConfig";
import { useIsAdminContentBuilderView } from "./useIsAdminContentBuilderView";
import { usePrimaryRailCollapsedStore } from "./usePrimaryRailCollapsedStore";

const AdminLeftNav: React.FC<{ compact?: boolean }> = ({
  compact: compactView = false,
}) => {
  const { pathname } = useRouter();
  const { open, primaryRailPinned, isMobile, setOpen } = useSidebar();
  const railCollapsed = usePrimaryRailCollapsedStore(
    (state) => state.collapsed,
  );
  const setRailCollapsed = usePrimaryRailCollapsedStore(
    (state) => state.setCollapsed,
  );
  // Below md the nav is the same rail, compact as on full-screen views: no
  // off-canvas sheet, so the agent panel always slides out beside it.
  const compact = compactView || isMobile;
  const wasContentBuilderView = useRef(false);
  const { userActionPermissions } = useActionPermissions();
  const { user } = useUser();
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  const guestFeedbackEnabled = useFeatureFlag(Ff.GuestFeedbackCorrectiveAction);
  const guestFeedbackRecommendationsEnabled = useFeatureFlag(
    Ff.GuestFeedbackRecommendations,
  );
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const newMessagingEnabled = useFeatureFlag(Ff.NewMessaging);
  const pathsAsWorkflowsEnabled = useFeatureFlag(Ff.PathsAsWorkflows);
  const workflowsEnabled = useFeatureFlag(Ff.WorkflowsTab);
  const mcpConnectorEnabled = useFeatureFlag(Ff.McpConnector);
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  // Builders and full-screen views collapse the secondary menu on entry and
  // restore it on the way out.
  const isContentBuilderView = useIsAdminContentBuilderView() || compact;

  const openBeforeCollapse = useRef(open);
  useLayoutEffect(() => {
    if (isContentBuilderView) {
      if (!wasContentBuilderView.current) {
        wasContentBuilderView.current = true;
        openBeforeCollapse.current = open;
        setOpen(false);
      }
      return;
    }
    if (wasContentBuilderView.current) {
      wasContentBuilderView.current = false;
      setOpen(openBeforeCollapse.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- collapse only when entering builder
  }, [isContentBuilderView]);

  const visibilityContext = useMemo(
    () => ({
      permissions: userActionPermissions,
      featureFlags: {
        [Ff.OpusAgent]: opusAgentEnabled,
        [Ff.Audits]: auditsEnabled,
        [Ff.TaskLibraryItemType]: taskLibraryItemTypeEnabled,
        [Ff.GuestFeedbackCorrectiveAction]: guestFeedbackEnabled,
        [Ff.GuestFeedbackRecommendations]: guestFeedbackRecommendationsEnabled,
        [Ff.NewMessaging]: newMessagingEnabled,
        [Ff.PathsAsWorkflows]: pathsAsWorkflowsEnabled,
        [Ff.WorkflowsTab]: workflowsEnabled,
        [Ff.McpConnector]: mcpConnectorEnabled,
      },
      billingInitialized,
      userType: user?.userType,
    }),
    [
      opusAgentEnabled,
      auditsEnabled,
      taskLibraryItemTypeEnabled,
      billingInitialized,
      guestFeedbackEnabled,
      guestFeedbackRecommendationsEnabled,
      newMessagingEnabled,
      pathsAsWorkflowsEnabled,
      workflowsEnabled,
      mcpConnectorEnabled,
      userActionPermissions,
      user?.userType,
    ],
  );
  const visiblePrimaryItems = useMemo(
    () => getVisiblePrimaryItems(visibilityContext),
    [visibilityContext],
  );
  const routeActiveItem = useMemo(
    () => findActivePrimaryItem(pathname, visiblePrimaryItems),
    [pathname, visiblePrimaryItems],
  );
  const [selectedSectionId, setSelectedSectionId] = useState<AdminNavSectionId>(
    routeActiveItem.id,
  );

  useEffect(() => {
    setSelectedSectionId(routeActiveItem.id);
  }, [routeActiveItem.id]);

  const activeItem =
    selectedSectionId === "settings"
      ? adminSettingsNavItem
      : selectedSectionId === "accountPlan"
        ? adminAccountPlanNavItem
        : visiblePrimaryItems.find((item) => item.id === selectedSectionId) ||
          routeActiveItem;
  const visibleSecondaryItems = useMemo(
    () => getVisibleSecondaryItems(activeItem.items, visibilityContext),
    [activeItem.items, visibilityContext],
  );
  const hasSecondaryMenu =
    activeItem.id === "reporting" ||
    activeItem.id === "recommendations" ||
    visibleSecondaryItems.length > 0;

  // Without a secondary menu the rail is the whole nav, so it stays expanded
  // as a persistent column unless the user collapsed it to the icon rail.
  // A (legacy) pinned rail is always expanded, so there is nothing to toggle.
  const collapsibleRail =
    !hasSecondaryMenu && !compact && !(primaryRailPinned && !isMobile);
  const forcePrimaryRailExpanded = collapsibleRail && !railCollapsed;

  // ⌘B toggles the secondary menu where there is one; here it toggles the
  // rail itself, with the same guards as SidebarProvider's shortcut. The
  // capture-phase listener runs first and stops SidebarProvider's from also
  // flipping the (hidden) secondary menu, which would arrive closed on the
  // next section that has one.
  useEffect(() => {
    if (!collapsibleRail) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "b" || !(event.metaKey || event.ctrlKey)) return;
      if (
        event.target instanceof Element &&
        (event.target.matches("input, textarea") ||
          event.target.classList.contains("ProseMirror") ||
          event.target.closest("[contenteditable]"))
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const { collapsed, setCollapsed } =
        usePrimaryRailCollapsedStore.getState();
      setCollapsed(!collapsed);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [collapsibleRail]);

  const railWidth =
    (primaryRailPinned && !isMobile) || forcePrimaryRailExpanded
      ? "var(--admin-primary-rail-expanded-width)"
      : "var(--admin-primary-rail-collapsed-width)";
  // Below md an open secondary menu overlays the page rather than squeezing it.
  const reservedWidth = `calc(${railWidth} + ${
    hasSecondaryMenu && open && !isMobile
      ? "var(--admin-secondary-menu-width) + 8px"
      : "0px"
  })`;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegation only; links handle their own keys */}
      <div
        // Below md the overlaid secondary menu closes once a link in it is
        // followed. Not on URL changes: a rail section opens the menu and
        // navigates in the same step, and the app rewrites the URL after.
        onClick={(event) => {
          if (
            isMobile &&
            (event.target as HTMLElement).closest("aside a[href]")
          )
            setOpen(false);
        }}
        className="relative h-[calc(100svh-var(--impersonation-banner-height))] shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none"
        style={{ width: reservedWidth }}
      >
        <AdminPrimaryNavRail
          items={visiblePrimaryItems}
          activeSectionId={activeItem.id}
          onSelectSection={setSelectedSectionId}
          forceExpanded={forcePrimaryRailExpanded}
          onToggleCollapsed={
            collapsibleRail ? () => setRailCollapsed(!railCollapsed) : undefined
          }
          settingsHref={getSettingsLandingHref(visibilityContext)}
        />
        {hasSecondaryMenu && (
          <AdminSecondaryNavMenu
            activeItem={activeItem}
            visibilityContext={visibilityContext}
            collapsed={compact}
          />
        )}
      </div>
    </>
  );
};

export default AdminLeftNav;
